import React, { useState, useEffect } from 'react';
import { 
  X, 
  Save, 
  Plus, 
  Trash2, 
  IndianRupee, 
  Clock, 
  AlertTriangle,
  Loader2
} from 'lucide-react';

const AddEditServiceModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  service = null, 
  categories = [],
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    categoryId: '',
    basePrice: '',
    durationMin: '',
    description: '',
    active: true,
    addons: [],
    regions: []
  });

  const [errors, setErrors] = useState({});
  const [newAddon, setNewAddon] = useState({ title: '', price: '', description: '' });
  const [newRegion, setNewRegion] = useState({ state: '', language: '', priceModifier: '' });
  const [showAddonForm, setShowAddonForm] = useState(false);
  const [showRegionForm, setShowRegionForm] = useState(false);

  const isEditMode = !!service;

  useEffect(() => {
    if (service) {
      setFormData({
        title: service.title || '',
        categoryId: service.categoryId || '',
        basePrice: service.basePrice?.toString() || '',
        durationMin: service.durationMin?.toString() || '',
        description: service.description || '',
        active: service.active ?? true,
        addons: service.addons || [],
        regions: service.regions || []
      });
    } else {
      setFormData({
        title: '',
        categoryId: '',
        basePrice: '',
        durationMin: '',
        description: '',
        active: true,
        addons: [],
        regions: []
      });
    }
    setErrors({});
  }, [service, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Service title is required';
    }

    if (!formData.categoryId) {
      newErrors.categoryId = 'Category is required';
    }

    if (!formData.basePrice || parseFloat(formData.basePrice) <= 0) {
      newErrors.basePrice = 'Valid base price is required';
    }

    if (!formData.durationMin || parseInt(formData.durationMin) <= 0) {
      newErrors.durationMin = 'Valid duration is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      basePrice: parseFloat(formData.basePrice),
      durationMin: parseInt(formData.durationMin)
    };

    onSave(submitData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const addAddon = () => {
    if (newAddon.title && newAddon.price) {
      setFormData(prev => ({
        ...prev,
        addons: [...prev.addons, { ...newAddon, price: parseFloat(newAddon.price) }]
      }));
      setNewAddon({ title: '', price: '', description: '' });
      setShowAddonForm(false);
    }
  };

  const removeAddon = (index) => {
    setFormData(prev => ({
      ...prev,
      addons: prev.addons.filter((_, i) => i !== index)
    }));
  };

  const addRegion = () => {
    if (newRegion.state && newRegion.language) {
      setFormData(prev => ({
        ...prev,
        regions: [...prev.regions, {
          ...newRegion,
          priceModifier: newRegion.priceModifier ? parseFloat(newRegion.priceModifier) : null
        }]
      }));
      setNewRegion({ state: '', language: '', priceModifier: '' });
      setShowRegionForm(false);
    }
  };

  const removeRegion = (index) => {
    setFormData(prev => ({
      ...prev,
      regions: prev.regions.filter((_, i) => i !== index)
    }));
  };

  if (!isOpen) return null;

  return (
    <div translate="no" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditMode ? 'Edit Service' : 'Add New Service'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="bg-orange-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-orange-800 mb-4">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Satyanarayan Puja"
                  disabled={loading}
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  value={formData.categoryId}
                  onChange={(e) => handleInputChange('categoryId', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                    errors.categoryId ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading}
                >
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {errors.categoryId && (
                  <p className="mt-1 text-sm text-red-600">{errors.categoryId}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base Price (₹) *
                </label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.basePrice}
                    onChange={(e) => handleInputChange('basePrice', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      errors.basePrice ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="1500"
                    disabled={loading}
                  />
                </div>
                {errors.basePrice && (
                  <p className="mt-1 text-sm text-red-600">{errors.basePrice}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration (minutes) *
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="number"
                    min="1"
                    value={formData.durationMin}
                    onChange={(e) => handleInputChange('durationMin', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      errors.durationMin ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="120"
                    disabled={loading}
                  />
                </div>
                {errors.durationMin && (
                  <p className="mt-1 text-sm text-red-600">{errors.durationMin}</p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Detailed description of the service..."
                disabled={loading}
              />
            </div>

            <div className="mt-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => handleInputChange('active', e.target.checked)}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  disabled={loading}
                />
                <span className="ml-2 text-sm text-gray-700">Active Service</span>
              </label>
            </div>
          </div>

          {/* Service Addons */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-blue-800">Service Add-ons</h3>
              <button
                type="button"
                onClick={() => setShowAddonForm(!showAddonForm)}
                className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                disabled={loading}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Add-on
              </button>
            </div>

            {showAddonForm && (
              <div className="bg-white p-4 rounded-lg border border-blue-200 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input
                    type="text"
                    placeholder="Add-on title"
                    value={newAddon.title}
                    onChange={(e) => setNewAddon(prev => ({ ...prev, title: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Price"
                    value={newAddon.price}
                    onChange={(e) => setNewAddon(prev => ({ ...prev, price: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={newAddon.description}
                    onChange={(e) => setNewAddon(prev => ({ ...prev, description: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex justify-end mt-3 space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowAddonForm(false)}
                    className="px-3 py-1 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={addAddon}
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}

            {formData.addons.length > 0 && (
              <div className="space-y-2">
                {formData.addons.map((addon, index) => (
                  <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border border-blue-200">
                    <div>
                      <span className="font-medium">{addon.title}</span>
                      <span className="ml-2 text-blue-600">₹{addon.price}</span>
                      {addon.description && (
                        <p className="text-sm text-gray-600">{addon.description}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAddon(index)}
                      className="text-red-500 hover:text-red-700"
                      disabled={loading}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Regional Pricing */}
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-green-800">Regional Pricing</h3>
              <button
                type="button"
                onClick={() => setShowRegionForm(!showRegionForm)}
                className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                disabled={loading}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Region
              </button>
            </div>

            {showRegionForm && (
              <div className="bg-white p-4 rounded-lg border border-green-200 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input
                    type="text"
                    placeholder="State"
                    value={newRegion.state}
                    onChange={(e) => setNewRegion(prev => ({ ...prev, state: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <input
                    type="text"
                    placeholder="Language"
                    value={newRegion.language}
                    onChange={(e) => setNewRegion(prev => ({ ...prev, language: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Price Modifier (optional)"
                    value={newRegion.priceModifier}
                    onChange={(e) => setNewRegion(prev => ({ ...prev, priceModifier: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div className="flex justify-end mt-3 space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowRegionForm(false)}
                    className="px-3 py-1 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={addRegion}
                    className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}

            {formData.regions.length > 0 && (
              <div className="space-y-2">
                {formData.regions.map((region, index) => (
                  <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border border-green-200">
                    <div>
                      <span className="font-medium">{region.state}</span>
                      <span className="ml-2 text-green-600">({region.language})</span>
                      {region.priceModifier && (
                        <span className="ml-2 text-sm text-gray-600">
                          Modifier: {region.priceModifier > 0 ? '+' : ''}{region.priceModifier}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRegion(index)}
                      className="text-red-500 hover:text-red-700"
                      disabled={loading}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="flex items-center">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {loading
                  ? (isEditMode ? 'Updating...' : 'Creating...')
                  : (isEditMode ? 'Update Service' : 'Create Service')}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditServiceModal;
